import {kml, gpx} from '@tmcw/togeojson'
import ExifReader from 'exifreader'
import JSZip from 'jszip'
import FitParser from 'fit-file-parser'
import shp, {parseShp, parseDbf} from 'shpjs'
import {bbox} from '@turf/turf'

function getImageGeometry(gps) {
  let latitude = gps.Latitude || gps.GPSLatitude.description
  let longitude = gps.Longitude || gps.GPSLongitude.description

  if (gps.GPSLatitudeRef && gps.GPSLatitudeRef.value[0] === 'S') {
    latitude = -latitude
  }

  if (gps.GPSLongitudeRef && gps.GPSLongitudeRef.value[0] === 'W') {
    longitude = -longitude
  }

  latitude = Math.max(-90, Math.min(90, latitude))
  longitude = Math.max(-180, Math.min(180, longitude))

  const geometry = {
    type: 'Point',
    coordinates: [
      longitude,
      latitude
    ]
  }

  return geometry
}

function parseDate(s) {
  const b = s.split(/\D/)
  return new Date(b[0], b[1]-1, b[2], b[3], b[4], b[5])
}

function parseXML(data) {
  try {
    const cleanData = data.replace(/xsi:schemaLocation=["'][^"']*["']\s*/g, '')

    const parser = new DOMParser()
    return parser.parseFromString(cleanData, 'application/xml')
  } catch (e) {
    console.error('Error parsing XML data:', e)
    return null
  }
}

export async function readFile(file, readAsBuffer = false) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (event) => {
      resolve(event.target.result)
    }

    reader.onerror = (event) => {
      reject(event.target.error)
    }

    if (readAsBuffer) {
      return reader.readAsText(file)
    }
    return reader.readAsArrayBuffer(file)
  })
}

export const parseKML = async (file) => {
  const contents = await readFile(file, true)
  const xml = parseXML(contents)

  try {
    const featureCollection = kml(xml)
    const features = featureCollection.features.map(feature => {
      feature.properties = {
        ...feature.properties,
        name: feature.properties?.name || feature.properties?.Name || file.name
      }
      return feature
    })
    return {
      collection: {
        type: 'FeatureCollection',
        features: features
      },
      name: file.name
    }
  } catch (error) {
    console.error("Error parsing .kml file:", error)
  }
}

export const parseKMZ = async (file) => {
  try {
    const zip = new JSZip()
    const archive = await zip.loadAsync(file)
    const kmlFiles = Object.keys(archive.files).filter(f => f.endsWith('.kml'))

    if (kmlFiles.length === 0) {
      console.warn("No KML files found in KMZ archive:", file.name)
      return null
    }

    for (let kmlFileName of kmlFiles) {
      const kmlFile = archive.files[kmlFileName]
      const kmlContents = await kmlFile.async('string')
      const xml = parseXML(kmlContents)

      if (!xml) {
        console.warn("Failed to parse XML from KML file:", kmlFileName)
        continue
      }

      try {
        const featureCollection = kml(xml)
        const features = featureCollection.features.map(feature => {
          feature.properties = {
            ...feature.properties,
            name: feature.properties?.name || feature.properties?.Name || file.name
          }
          return feature
        })

        return {
          collection: {
            type: 'FeatureCollection',
            features: features
          },
          name: file.name
        }
      } catch (kmlError) {
        console.error("Error parsing KML content from file:", kmlFileName, kmlError)
        continue
      }
    }

    console.error("No valid KML files could be parsed from KMZ:", file.name)
    return null
  } catch (error) {
    console.error("Error parsing .kmz file:", error)
    return null
  }
}

export const parseGeoJSON = async (file) => {
  const contents = await readFile(file, true)
  try {
    const parsed = JSON.parse(contents)
    let features = []

    if (parsed.type === 'FeatureCollection' && Array.isArray(parsed.features)) {
      features = parsed.features
    } else if (parsed.type === 'Feature') {
      features = [parsed]
    } else if (parsed.type && parsed.coordinates) {
      features = [{
        type: 'Feature',
        properties: {},
        geometry: parsed
      }]
    } else {
      console.error("Unrecognized GeoJSON structure:", parsed.type)
      return null
    }

    features = features.map(feature => {
      feature.properties = {
        ...feature.properties,
        name: feature.properties?.name || feature.properties?.Name || file.name
      }
      return feature
    })

    return {
      collection: {
        type: 'FeatureCollection',
        features: features
      },
      name: file.name
    }
  } catch (error) {
    console.error("Error parsing .geojson file:", error)
    return null
  }
}

export const parseImage = async (file) => {
  try {
    const tags = await ExifReader.load(file, { async: true })
    const created_origin = tags.DateTimeOriginal ? parseDate(tags.DateTimeOriginal.value[0]).getTime() : file.lastModified
    const geometry = tags.GPSLatitude && tags.GPSLongitude
      ? getImageGeometry({
          GPSLatitude: tags.GPSLatitude,
          GPSLongitude: tags.GPSLongitude,
          GPSLatitudeRef: tags.GPSLatitudeRef,
          GPSLongitudeRef: tags.GPSLongitudeRef
        })
      : null

    const feature = {
      type: 'Feature',
      properties: {
        name: file.name,
        description: '',
        created_origin: created_origin,
        type: 'Point',
        resource: file,
        ...(geometry ? {} : { needsLocation: true })
      },
      geometry: geometry
    }

    return {
      collection: {
        type: 'FeatureCollection',
        features: [feature]
      },
      name: file.name
    }
  } catch (error) {
    console.error("Error parsing image file:", error)
  }
}

export const parseGPX = async (file) => {
  const contents = await readFile(file, true)
  const xml = parseXML(contents)

  try {
    const featureCollection = gpx(xml)
    const features = featureCollection.features.map(feature => {
      feature.properties = {
        ...feature.properties,
        name: feature.properties?.name || feature.properties?.Name || file.name
      }
      return feature
    })
    return {
      collection: {
        type: 'FeatureCollection',
        features: features
      },
      name: file.name
    }
  } catch (error) {
    console.error("Error parsing .gpx file:", error)
  }
}

// shpjs returns either one FeatureCollection or an array of them for multi-layer zips.
function normalizeShpLayers(result, zipName) {
  const results = Array.isArray(result) ? result : [result]
  return results.map((fc) => {
    // shpjs's fileName is an extensionless path, e.g. "BioMA_LULC/BioMA_LULC_complete".
    const basename = fc.fileName ? fc.fileName.split('/').pop() : null
    return {
      name: basename || zipName,
      collection: { type: 'FeatureCollection', features: fc.features || [] }
    }
  })
}

// Heuristic: bbox outside WGS84 bounds suggests a projected CRS (missing .prj).
// False-negatives possible for small-area grids near 0,0; the warning is advisory.
function looksProjected(collection) {
  if (!collection.features.length) return false
  try {
    const [minX, minY, maxX, maxY] = bbox(collection)
    return minX < -180 || maxX > 180 || minY < -90 || maxY > 90
  } catch (e) {
    return false
  }
}

// Manual walk so .geojson entries are picked up too — shpjs only recognises `.json`.
export const parseShapefileZip = async (file) => {
  try {
    const zip = await new JSZip().loadAsync(file)

    const bundles = new Map()
    const geojsonEntries = []
    for (const [path, entry] of Object.entries(zip.files)) {
      if (entry.dir) continue
      if (path.includes('__MACOSX') || path.split('/').pop().startsWith('.')) continue
      const lower = path.toLowerCase()
      const dotIdx = lower.lastIndexOf('.')
      const ext = dotIdx >= 0 ? lower.slice(dotIdx + 1) : ''
      const stem = dotIdx >= 0 ? path.slice(0, dotIdx) : path

      if (ext === 'geojson' || ext === 'json') {
        geojsonEntries.push({ path, entry })
      } else if (['shp', 'shx', 'dbf', 'prj', 'cpg'].includes(ext)) {
        if (!bundles.has(stem)) bundles.set(stem, {})
        bundles.get(stem)[ext] = entry
      }
    }

    const layers = []
    let anyMissingPrj = false

    for (const entry of geojsonEntries) {
      try {
        const text = await entry.entry.async('string')
        const parsed = JSON.parse(text)
        let features = []
        if (parsed.type === 'FeatureCollection' && Array.isArray(parsed.features)) {
          features = parsed.features
        } else if (parsed.type === 'Feature') {
          features = [parsed]
        } else if (parsed.type && parsed.coordinates) {
          features = [{ type: 'Feature', properties: {}, geometry: parsed }]
        }
        if (features.length) {
          const basename = entry.path.split('/').pop().replace(/\.(geo)?json$/i, '')
          layers.push({
            name: basename,
            collection: { type: 'FeatureCollection', features }
          })
        }
      } catch (e) {
        console.warn(`Skipping unreadable JSON entry ${entry.path}:`, e)
      }
    }

    for (const [stem, parts] of bundles.entries()) {
      if (!parts.shp) continue
      try {
        const shpBuffer = await parts.shp.async('arraybuffer')
        const dbfBuffer = parts.dbf ? await parts.dbf.async('arraybuffer') : null
        const prjText = parts.prj ? await parts.prj.async('string') : null
        const cpgText = parts.cpg ? await parts.cpg.async('string') : null

        const geom = parseShp(shpBuffer, prjText || undefined)
        const attrs = dbfBuffer ? parseDbf(dbfBuffer, cpgText || undefined) : []
        const features = geom.map((g, i) => ({
          type: 'Feature',
          geometry: g,
          properties: attrs[i] || {}
        }))

        const basename = stem.split('/').pop()
        const collection = { type: 'FeatureCollection', features }
        if (!parts.prj && looksProjected(collection)) anyMissingPrj = true
        layers.push({ name: basename, collection })
      } catch (e) {
        console.error(`Failed to parse shapefile ${stem} inside zip:`, e)
      }
    }

    if (!layers.length) return null
    const warn = anyMissingPrj ? 'missing-prj' : null
    return { layers, warn }
  } catch (error) {
    console.error('Error parsing shapefile zip:', error)
    return null
  }
}

export const parseShapefileBundle = async ({shp: shpFile, dbf, prj, cpg, fileName}) => {
  try {
    const shpBuffer = await readFile(shpFile, false)
    const dbfBuffer = dbf ? await readFile(dbf, false) : undefined
    const prjText = prj ? await readFile(prj, true) : undefined
    const cpgText = cpg ? await readFile(cpg, true) : undefined

    const result = await shp({
      shp: shpBuffer,
      dbf: dbfBuffer,
      prj: prjText,
      cpg: cpgText
    })
    const layers = normalizeShpLayers(result, fileName)
    // Loose-file bundles are single-layer; prefer the user's stem over shpjs's name.
    for (const layer of layers) {
      layer.name = fileName
    }
    const warn = !prj && layers.some(l => looksProjected(l.collection)) ? 'missing-prj' : null
    return { layers, warn }
  } catch (error) {
    console.error('Error parsing shapefile bundle:', error)
    return null
  }
}

export const parseFIT = async (file) => {
  try {
    const buffer = await readFile(file, false)
    const parser = new FitParser({
      force: true,
      speedUnit: 'km/h',
      lengthUnit: 'km',
      temperatureUnit: 'celsius',
      elapsedRecordField: true,
      mode: 'list'
    })

    return new Promise((resolve, reject) => {
      parser.parse(buffer, (error, data) => {
        if (error) {
          console.error("Error parsing .fit file:", error)
          reject(error)
          return
        }

        const features = []
        if (data.records) {
          const coordinates = data.records
            .filter(record => record.position_lat !== undefined && record.position_long !== undefined)
            .map(record => {
              const lat = record.position_lat
              const lng = record.position_long
              return [lng, lat]
            })

          if (coordinates.length > 0) {
            features.push({
              type: 'Feature',
              properties: {
                name: file.name,
                description: '' // FIT files don't typically have descriptions, but prepare for it
              },
              geometry: {
                type: 'LineString',
                coordinates: coordinates
              }
            })
          }
        }

        resolve({
          collection: {
            type: 'FeatureCollection',
            features: features
          },
          name: file.name
        })
      })
    })
  } catch (error) {
    console.error("Error parsing .fit file:", error)
  }
}
